/**
 * Helper para gerar opções de tiebreak baseado no número de games por set.
 * 
 * @param gamesPorSets - Número de games por set (pode ser number, string ou null/undefined)
 * @returns Array de opções para o select de tiebreak
 */
export interface TiebreakOption {
  value: string;
  label: string;
}

export function getTiebreakOptions(gamesPorSets: number | string | null | undefined): TiebreakOption[] {
  // Converter para número se necessário
  let games: number | null = null;
  
  if (gamesPorSets == null || gamesPorSets === '') {
    return [];
  }
  
  if (typeof gamesPorSets === 'number') {
    games = gamesPorSets;
  } else if (typeof gamesPorSets === 'string') {
    games = parseInt(gamesPorSets, 10);
  } else {
    games = Number(gamesPorSets);
  }
  
  if (isNaN(games) || games < 1) {
    return [];
  }

  const options: TiebreakOption[] = [
    { value: 'null', label: 'Sem tiebreak' }, // Usar 'null' como string para distinguir de string vazia
  ];

  if (games === 1) {
    options.push({ value: '1', label: 'Tiebreak no 1 a 1' });
  } else if (games > 1) {
    const x = games - 1;
    const y = games;
    options.push({ value: x.toString(), label: `Tiebreak no ${x} a ${x}` });
    options.push({ value: y.toString(), label: `Tiebreak no ${y} a ${y}` });
  }

  return options;
}

/**
 * Converte o valor do select de tiebreak para o formato esperado pelo backend.
 * 
 * @param value - Valor do select (string vazia, 'null', ou número como string)
 * @returns number | null - Número do tiebreak ou null para "sem tiebreak"
 */
export function parseTiebreakValue(value: string): number | null {
  if (!value || value === '' || value === 'null') {
    return null;
  }
  const num = parseInt(value, 10);
  return isNaN(num) ? null : num;
}

/**
 * Retorna os valores numéricos de tiebreak válidos para um dado games por set.
 *
 * Fonte única de verdade derivada de `getTiebreakOptions` (descarta a opção
 * "Sem tiebreak"). Usada para checagem cruzada games-por-set × tiebreak no
 * schema e no formulário, evitando combinações inconsistentes (ex.: 6 games
 * por set com "tiebreak no 1 a 1", que tornaria 6x4 inválido).
 *
 * @param gamesPorSets - Número de games por set (number, string ou null/undefined)
 * @returns Array de valores numéricos de tiebreak válidos (vazio se gamesPorSets inválido)
 */
export function getValidTiebreakValues(gamesPorSets: number | string | null | undefined): number[] {
  return getTiebreakOptions(gamesPorSets)
    .map((option) => parseTiebreakValue(option.value))
    .filter((value): value is number => value !== null);
}

/**
 * Converte o valor do backend para o formato do select.
 *
 * @param value - Valor do backend (number | null | undefined)
 * @returns string - Valor para o select ('null' para null, ou número como string)
 */
export function formatTiebreakValue(value: number | null | undefined): string {
  if (value == null) {
    return 'null'; // Usar 'null' como string para "sem tiebreak"
  }
  return value.toString();
}








